import React from "react";
import { useTranslation } from "react-i18next";

/**
 * 英文版隐私协议内容
 */
export const PrivacyPolicyContentEn: React.FC = () => {
  return (
    <>
      <p>Last updated on January 8, 2026</p>
      <p>
        This privacy policy (&ldquo;Privacy Policy&rdquo;) describes the types
        of personal information that GenOptima / 智推时代 (&ldquo;GenOptima,
        &rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; and/or &ldquo;us&rdquo;)
        collects, uses, and discloses from individuals (&ldquo;you&rdquo; or
        &ldquo;your&rdquo;) who use our website (including
        https://www.gen-optima.com) and services that link to this Privacy
        Policy (collectively, our &ldquo;Services&rdquo;). As used in this
        Privacy Policy, &ldquo;personal information&rdquo; means any information
        relating to an identified or identifiable individual. By using our
        Services, you agree to the collection, use, disclosure, procedures, and
        other processing described in this Privacy Policy. Beyond the Privacy
        Policy, your use of our Services is also subject to our Terms of
        Service.
      </p>
      <p>
        This Privacy Policy does not apply to the extent we process personal
        information in the role of a processor or service provider on behalf of
        our customers, including where we offer to our customers our
        software-as-a-service platform. In that context, our customers are the
        data controllers, and our processing of that personal information is
        governed by our applicable customer contracts.
      </p>

      <h3>Personal Information We Collect</h3>
      <p>
        We may collect a variety of personal information from or about you or
        your devices from various sources, as described below. Where applicable,
        we indicate whether and why you must provide us with your personal
        information, as well as the consequences of failing to do so. If you do
        not provide your personal information when requested, you may not be
        able to use our Services if that personal information is necessary to
        provide you with our Services or if we are legally required to collect
        it.
      </p>

      <h4>A. Personal Information You Provide to Us</h4>
      <ul>
        <li>
          <strong>Contact Information.</strong> We may collect your personal
          information when you inquire about our Services or when you sign up
          for, attend, or take part in our demos, events, webinars, or programs.
          This information may include your full name, work email, company name,
          company size, and any information you choose to provide to us.
        </li>
        <li>
          <strong>Communications.</strong> If you contact us directly, we may
          receive information about you, such as your name, email address, the
          contents of a message or attachments that you may send to us, and
          other information you choose to provide.
        </li>
        <li>
          <strong>Account Information.</strong> We collect the information you
          provide to create, update, or manage your account on the Services,
          including your email address, phone number, and your name.
        </li>
        <li>
          <strong>Customer Inputs.</strong> You may provide prompts, queries,
          URLs, datasets, or other materials as part of using the Services. We
          collect this information to provide, maintain, and improve the
          Services.
        </li>
        <li>
          <strong>Payment Information.</strong> If you make a payment on our
          Services, your payment-related information, such as credit card or
          other financial information, is collected by our third-party payment
          processor on our behalf.
        </li>
      </ul>

      <h4>B. Personal Information We Collect When You Use Our Services</h4>
      <ul>
        <li>
          <strong>Location Information.</strong> When you use our Services, we
          infer your general location information, for example, by using your
          internet protocol (IP) address.
        </li>
        <li>
          <strong>Device Information.</strong> We receive information about the
          device and software you use to access our Services, including IP
          address, device type, device identifiers, web browser type and
          version, and operating system version.
        </li>
        <li>
          <strong>Usage Information.</strong> We automatically receive
          information about your interactions with our Services, like the pages
          or other content you view, referrer information, the dates and times
          of your visits, your log data, and the actions you take.
        </li>
        <li>
          <strong>Information from Cookies and Similar Technologies.</strong> We
          and our third-party partners may collect information using cookies,
          which are small files of letters and numbers that we store on your
          browser or the hard drive of your computer.
        </li>
      </ul>

      <h3>How We Use the Personal Information We Collect</h3>
      <p>We use the personal information we collect:</p>
      <ul>
        <li>To provide, maintain, improve, and enhance our Services;</li>
        <li>
          To personalize your experience on our Services such as by providing
          tailored content and recommendations;
        </li>
        <li>
          To understand and analyze how you use our Services and develop new
          products, services, features, and functionality;
        </li>
        <li>
          To communicate with you, provide you with updates and other
          information relating to our Services;
        </li>
        <li>For marketing and advertising purposes;</li>
        <li>
          To generate anonymized or aggregate data containing only
          de-identified, non-personal information;
        </li>
        <li>To facilitate transactions and payments;</li>
        <li>
          To find and prevent fraud and abuse, and respond to trust and safety
          issues;
        </li>
        <li>
          For compliance purposes, including enforcing our Terms of Service or
          other legal rights.
        </li>
      </ul>

      <h3>Legal Bases for Processing European Personal Information</h3>
      <p>
        If you are located in the European Economic Area (&ldquo;EEA&rdquo;) or
        the United Kingdom (&ldquo;UK&rdquo;), we only process your personal
        information when we have a valid &ldquo;legal basis,&rdquo; including
        Consent, Contractual Necessity, Compliance with a Legal Obligation, and
        Legitimate Interests.
      </p>

      <h3>How We Disclose the Personal Information We Collect</h3>
      <p>
        We do not sell your personal information to third parties. We may
        disclose the personal information we collect to affiliates and
        subsidiaries, third-party service providers, third-party AI model
        providers, or in the event of a merger or acquisition.
      </p>

      <h3>Your Choices</h3>
      <p>
        In certain circumstances, you may have the following rights in relation
        to your personal information: Access, Correction, Deletion, Restriction,
        Objection, and Withdraw Consent. You may exercise any of these rights by
        contacting us at the contact information below.
      </p>

      <h3>Third Parties</h3>
      <p>
        Our Services may contain links to third-party websites and applications.
        Any information you provide to those third parties is not subject to the
        terms of this Privacy Policy, and we are not responsible for the privacy
        practices of those third parties.
      </p>

      <h3>Retention</h3>
      <p>
        We retain personal information for as long as necessary to fulfill the
        purposes for which we collect it, such as to provide you with the
        Services you have requested, and for the purpose of satisfying any
        legal, accounting, contractual, or reporting requirements.
      </p>

      <h3>Security</h3>
      <p>
        We use reasonable administrative, technical, and physical security
        measures to protect your personal information. However, no method of
        transmission over the internet or method of electronic storage is
        completely secure, and we cannot guarantee absolute security.
      </p>

      <h3>Children&apos;s Privacy</h3>
      <p>
        Our Services are intended for business and professional use and are not
        directed to children under the age of 18. We do not knowingly collect
        personal information from children under 18.
      </p>

      <h3>International Visitors</h3>
      <p>
        Our Services are hosted in the United States. If you are visiting from
        the European Union or other regions with laws governing data collection
        and use that may differ from United States law, please note that you are
        transferring your personal information to the United States.
      </p>

      <h3>Changes to this Privacy Policy</h3>
      <p>
        We may update this Privacy Policy from time to time. If we make changes,
        we will notify you by revising the date at the top of this Privacy
        Policy.
      </p>

      <h3>Contact Information</h3>
      <p>
        If you have any questions about this Privacy Policy, please contact us
        at: <a href="mailto:info@gen-optima.com">info@gen-optima.com</a>
      </p>
    </>
  );
};

/**
 * 中文版隐私协议内容
 */
export const PrivacyPolicyContentZh: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <p>
        保护您的隐私是 GenOptima
        的核心承诺。本条款说明我们如何处理您的信息、您的权益以及我们为保障信息安全所做的努力。
      </p>

      <h3>{t("common.modals.term_1_rights")}</h3>
      <p>
        作为信息的所有者，您依法享有多项权利：可随时向我们申请查询个人信息处理记录；若发现信息有误，有权要求更正；如您认为信息无需保留，可申请删除相关数据。
      </p>

      <h3>{t("common.modals.term_2_collection")}</h3>
      <ul>
        <li>
          主动提供信息：当您完成注册、提交咨询、订阅资讯等操作时，我们会收集您填写的姓名、邮箱、所属单位等内容。
        </li>
        <li>
          系统自动获取的技术信息：包括您的 IP
          地址、浏览器型号、设备操作系统、页面停留时长等，这些信息仅用于分析服务使用状态。
        </li>
      </ul>

      <h3>{t("common.modals.term_3_info_use")}</h3>
      <ul>
        <li>提供和维护服务：例如根据您提供的注册信息为您开通账号权限；</li>
        <li>优化服务体验：例如结合使用数据改进功能布局，提高操作便捷性；</li>
        <li>
          兑现服务承诺：通过您提供的联系方式发送服务更新，或应您请求提供售后支持（如您不愿接收，可随时在邮件中取消）。
        </li>
      </ul>

      <h3>{t("common.modals.term_4_security")}</h3>
      <p>
        我们采用多层次防护机制保障您的信息安全：技术层面实施数据加密存储、访问日志实时监控；管理层建立严格的权限和审计制度，仅授权必要人员处理数据。同时，我们会定期开展安全评审，及时修复潜在漏洞，尽力防范信息被非法获取、篡改或泄露。
      </p>

      <h3>{t("common.modals.term_5_cookies")}</h3>
      <p>
        为提升服务体验，我们会使用 Cookie 及本地存储等技术：会话 Cookie
        用于记录您的当前登录状态，关闭浏览器后自动失效；持久 Cookie
        可帮助记住您的偏好设置（如语言选择），默认保存不超过 1
        年。您可在浏览器隐私设置中选择禁用或删除
        Cookie，这可能影响部分功能的正常使用。
      </p>

      <h3>{t("common.modals.term_6_third_party")}</h3>
      <p>
        部分服务可能需要第三方协助完成，例如受托云服务商存储数据、访问分析工具统计访问量。我们会与合作方签订严格的数据保护协议，明确其仅在授权范围内处理信息，并要求其采取等同水平的安全措施。如发现存在违规处理行为，我们会立即终止合作，并通知您。
      </p>

      <h3>{t("common.modals.term_7_contact")}</h3>
      <p>
        如您对本条款或个人信息处理有任何问题、需行使权利或提交投诉，欢迎通过邮箱{" "}
        <a href="mailto:cmz@gen-optima.com">cmz@gen-optima.com</a> 与我们联系。
      </p>

      <p className="last-updated">{t("common.modals.last_updated_privacy")}</p>
    </>
  );
};
